import {isVariantChangeImmediateEvent, PUB_SUB_EVENTS} from '@/scripts/core/global'
import { isVariantChangeEvent, publish, subscribe } from '@/scripts/core/global'
import { type VariantRadios } from '@/scripts/theme/variant-radios'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { ProductStickyCta } from '@/scripts/product/product-sticky-cta'

interface VariantInventoryLimit {
	date: string;
	description: string;
}

export class ProductInfo extends UcoastEl {
	static htmlSelector = 'product-info'
	static selectors = {
		quantityForm: '[data-uc-product-form-quantity]',
		quantityInput: '[data-uc-quantity-input]',
		quantityRules: '[data-uc-quantity-rules]',
		quantityRulesCart: '[data-uc-quantity-rules-cart]',
		quantityLabel: '[data-uc-quantity-label]',
		currentVariant: '[data-uc-product-variant-id]',
		variantRadios: 'variant-radios',
		variantInventoryLimitData: '[data-uc-product-variant-inventory-limit]',
		variantInventoryLimitTable: '[data-uc-product-variant-inventory-limit-table]',
    upsellCards: '[data-uc-product-upsells-variant]',
		submitButton: '[type="submit"]',
    tabButtons: '[data-uc-product-info-tab-button]',
    tabContents: '[data-uc-product-info-tab-content]',
	}
	source: string
	productId: string
	quantityInput?: HTMLInputElement
	currentVariant: HTMLInputElement
	variantRadios?: VariantRadios
	submitButton: HTMLButtonElement
	quantityForm?: HTMLFormElement
	siblingForm?: ProductInfo | ProductStickyCta
	cartUpdateUnsubscriber?: () => void = undefined
	variantChangeUnsubscriber?: () => void = undefined
  variantChangeImmediateUnsubscriber?: () => void = undefined
	scrollCompareValue: number = 0
	isSticky: boolean
	stickyContainer?: HTMLElement
  tabButtons?: NodeListOf<HTMLElement>
  tabContents?: NodeListOf<HTMLElement>

  variantInventoryLimits: Record<string, VariantInventoryLimit[]> = {}

	constructor() {
		super()
		this.quantityInput = q.os(ProductInfo.selectors.quantityInput, this)
		this.currentVariant = q.rs(ProductInfo.selectors.currentVariant, this)
		this.variantRadios = q.os(ProductInfo.selectors.variantRadios, this)
		this.submitButton = q.rs(ProductInfo.selectors.submitButton, this)
		this.tabButtons = q.ol(ProductInfo.selectors.tabButtons, this)
		this.tabContents = q.ol(ProductInfo.selectors.tabContents, this)
		this.source = q.ra(this, 'data-uc-product-info-source')
		this.productId = q.ra(this, 'data-uc-product-id')
		this.siblingForm = this.getSiblingForm()
		this.isSticky = this.hasAttribute('data-uc-product-info-sticky-el')
		this.stickyContainer = q.os('[data-uc-product-info-sticky-container]')
		const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile'
		const announcementHeight = q.os('.announcement-bar-section')?.offsetHeight ?? 0

		if (this.isSticky && this.stickyContainer && viewport === 'desktop') {
			const containerBottomPos = this.stickyContainer.offsetTop + this.stickyContainer.offsetHeight
			this.scrollCompareValue = containerBottomPos - this.offsetTop - this.offsetHeight - announcementHeight
			requestAnimationFrame(this.checkSticky.bind(this))
		}

    try {
      this.variantInventoryLimits = JSON.parse(q.os(ProductInfo.selectors.variantInventoryLimitData, this)?.innerHTML ?? '{}');
    } catch (err) {
      console.error(err);
      this.variantInventoryLimits = {};
    }

    this.tabButtons?.forEach(button =>
      button.addEventListener('click', this.activateTab.bind(this))
    )
	}

	checkSticky() {
		if (window.scrollY >= this.scrollCompareValue) {
			this.classList.add('scrolled')
		} else {
			this.classList.remove('scrolled')
		}
		requestAnimationFrame(this.checkSticky.bind(this))
	}

	override connectedCallback() {
		if (!this.quantityInput) return
		this.quantityForm = q.os(ProductInfo.selectors.quantityForm, this)
		if (!this.quantityForm) return
		this.setQuantityBoundaries()
		if (!this.dataset.originalSection) {
			this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this))
		}
		this.source = q.ra(this, 'data-uc-product-info-source')

		this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
			if (!isVariantChangeEvent(event)) return
			const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
			if (event.data.sectionId !== sectionId) return
			this.updateQuantityRules(event.data.sectionId, event.data.html)
			this.setQuantityBoundaries()
			if (this.quantityInput && event.data.currentQuantity) {
				this.quantityInput.value = `${event.data.currentQuantity}`
				this.quantityInput.setAttribute('value', `${event.data.currentQuantity}`)
			}
		})

    this.variantChangeImmediateUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChangeImmediate, (event) => {
      if (!isVariantChangeImmediateEvent(event)) {
        return;
      }
      const { variantId } = event.data;
      this.updateVariantInventoryLimits(variantId.toString());
      this.showUpsellCardsOfCurrentVariant(variantId.toString());
    });
	}

	override disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber()
		}
		if (this.variantChangeUnsubscriber) {
			this.variantChangeUnsubscriber()
		}
    this.variantChangeImmediateUnsubscriber?.();
	}

	setQuantityBoundaries() {
		if (!this.quantityInput) return
		const data = {
			cartQuantity: this.quantityInput.dataset.cartQuantity
				? parseInt(this.quantityInput.dataset.cartQuantity)
				: 0,
			min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
			max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
			step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
		}

		let min = data.min
		const max = data.max === null ? data.max : data.max - data.cartQuantity
		if (max !== null) min = Math.min(min, max)
		if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

		this.quantityInput.min = `${min}`
		this.quantityInput.max = `${max}`
		this.quantityInput.value = `${min}`
		publish(PUB_SUB_EVENTS.quantityRangeUpdate, undefined)
    publish(PUB_SUB_EVENTS.quantityValueUpdate, { source: 'quantity-change', data: { quantity: min }});
	}

	fetchQuantityRules() {
		if (!this.currentVariant || !this.currentVariant.value) return
		q.rs('[data-uc-quantity-rules-cart] [data-uc-loading-overlay]', this).classList.remove('hidden')
		fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
			.then((response) => {
				return response.text()
			})
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, 'text/html')
				const sectionId = q.ra(this, 'data-section')
				this.updateQuantityRules(sectionId, html)
				this.setQuantityBoundaries()
			})
			.catch((e) => {
				console.error(e)
			})
			.finally(() => {
				q.rs('[data-uc-quantity-rules-cart] [data-uc-loading-overlay]', this).classList.add('hidden')
			})
	}

	updateQuantityRules(sectionId: string, html: Document) {
		const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`)
		const selectors = [
			ProductInfo.selectors.quantityInput,
			ProductInfo.selectors.quantityRules,
			ProductInfo.selectors.quantityLabel,
		]
		for (let selector of selectors) {
			if (!quantityFormUpdated) continue
			const current = q.os(selector, this.quantityForm)
			const updated = q.os(selector, quantityFormUpdated)
			if (!current || !updated) continue
			if (selector === ProductInfo.selectors.quantityInput) {
				const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
				for (let attribute of attributes) {
					const valueUpdated = updated.getAttribute(attribute)
					if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated)
				}
			} else {
				current.innerHTML = updated.innerHTML
			}
		}
	}

	getSiblingForm() {
		return q.os<ProductStickyCta>(`product-sticky-cta[data-uc-product-id="${this.productId}"]`)
	}

	updateSiblingForm() {
		if (this.source == 'quick-add') return
    return // this isn't used anymore, we had it for sticky bar
		/*if (!this.variantRadios) {
			throw new Error('should not be able to trigger update without radios')
		}
		if (!this.siblingForm) {
			throw new Error('should not be triggered, this element has no sibling form')
		}
		console.log('this.siblingForm', this.siblingForm)
		const selectedOptions = this.variantRadios.getSelectedOptions()
		const siblingFormRadios = this.siblingForm?.variantRadios
		if (!siblingFormRadios) {
			throw new Error('should not be triggered, this elements sibling form has no radios')
		}
		siblingFormRadios.updateOptionsFromExternalSource(selectedOptions)*/
	}

  updateVariantInventoryLimits(variantId: string) {
    // hide if no limits
    const limits = this.variantInventoryLimits[variantId];
    const table = q.os(ProductInfo.selectors.variantInventoryLimitTable);
    if (!table) {
      return;
    }
    if (!limits || limits.length === 0) {
      table.classList.add('hidden');
      return;
    }

    const body = q.rs('.variant-inventory-limit-table__body', table);
    body.replaceChildren(
      ...limits.flatMap((limit) => {
        const date = document.createElement('div');
        date.classList.add('variant-inventory-limit-table__date');
        date.innerHTML = limit.date;

        const description = document.createElement('div');
        description.classList.add('variant-inventory-limit-table__description');
        description.innerHTML = limit.description;

        return [date, description];
      })
    );

    table.classList.remove('hidden');
  }

  showUpsellCardsOfCurrentVariant(variantId: string) {
    const upsellCards = q.ol(ProductInfo.selectors.upsellCards, this);
    upsellCards?.forEach((card) => {
      const cardVariantId = card.dataset.ucProductUpsellsVariant;
      card.style.display = cardVariantId === variantId ? 'block' : 'none';
    });
  }

  activateTab(event: Event) {
    event.preventDefault()

    const target = q.rt<Event, HTMLButtonElement>(event)
    const selected = q.oa(target, 'data-uc-product-info-tab-button')

    this.tabButtons?.forEach(button => {
      const name = q.oa(button, 'data-uc-product-info-tab-button')

      button.classList.toggle('active', name === selected)
    })

    this.tabContents?.forEach(content => {
      const name = q.oa(content, 'data-uc-product-info-tab-content')

      content.classList.toggle('active', name === selected)
    })
  }
}
